import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { FileDownload as DownloadIcon } from '@mui/icons-material';

interface ExportButtonProps {
  data: any[];
  filename: string;
  headers?: string[];
  disabled?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  filename,
  headers,
  disabled = false,
  variant = 'outlined',
  size = 'medium',
}) => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const convertToCSV = (data: any[], headers?: string[]): string => {
    if (data.length === 0) return '';

    // Get headers from first object if not provided
    const csvHeaders = headers || Object.keys(data[0]);

    // Create CSV header row
    const headerRow = csvHeaders.join(',');

    // Create CSV data rows
    const dataRows = data.map((row) => {
      return csvHeaders
        .map((header) => {
          const value = row[header];
          // Handle values that contain commas, quotes, or newlines
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(',');
    });

    return [headerRow, ...dataRows].join('\n');
  };

  const handleExport = async () => {
    try {
      setExporting(true);

      // Convert data to CSV
      const csv = convertToCSV(data, headers);

      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={handleExport}
        disabled={disabled || exporting || data.length === 0}
      >
        {exporting ? 'Exporting...' : 'Export CSV'}
      </Button>
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
